import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Layout from "./components/Layout";

const Index = lazy(() => import("./pages/Index"));
const Haushaltsreinigung = lazy(
  () => import("./pages/leistungen/Haushaltsreinigung"),
);
const HaushaltsreinigungV2 = lazy(
  () => import("./pages/leistungen/HaushaltsreinigungV2"),
);
const Bueroreinigung = lazy(() => import("./pages/leistungen/Bueroreinigung"));
const Praxisreinigung = lazy(
  () => import("./pages/leistungen/Praxisreinigung"),
);
const Baureinigung = lazy(() => import("./pages/leistungen/Baureinigung"));
const Buegelservice = lazy(() => import("./pages/leistungen/Buegelservice"));
const Fensterreinigung = lazy(
  () => import("./pages/leistungen/Fensterreinigung"),
);
const Grundreinigung = lazy(() => import("./pages/leistungen/Grundreinigung"));
const Umzugreinigung = lazy(() => import("./pages/leistungen/Umzugreinigung"));
const Steinreinigung = lazy(() => import("./pages/leistungen/Steinreinigung"));
const Polsterreinigung = lazy(
  () => import("./pages/leistungen/Polsterreinigung"),
);
const Treppenhausreinigung = lazy(
  () => import("./pages/leistungen/Treppenhausreinigung"),
);
const Leistungen = lazy(() => import("./pages/leistungen/Leistungen"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Kontakt = lazy(() => import("./pages/Kontakt"));
const Preise = lazy(() => import("./pages/Preise"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Blog = lazy(() => import("./pages/blog/Blog"));
const BlogArtikel1 = lazy(() => import("./pages/blog/BlogArtikel1"));
const BlogArtikel2 = lazy(() => import("./pages/blog/BlogArtikel2"));
const BlogArtikel3 = lazy(() => import("./pages/blog/BlogArtikel3"));
const BlogArtikel4 = lazy(() => import("./pages/blog/BlogArtikel4"));
const BlogArtikel5 = lazy(() => import("./pages/blog/BlogArtikel5"));
const BlogArtikel6 = lazy(() => import("./pages/blog/BlogArtikel6"));
const TerminVerschieben = lazy(() => import("./pages/TerminVerschieben"));
const Graefelfing = lazy(() => import("./pages/standorte/Graefelfing"));
const Planegg = lazy(() => import("./pages/standorte/Planegg"));
const Gauting = lazy(() => import("./pages/standorte/Gauting"));
const Neuried = lazy(() => import("./pages/standorte/Neuried"));
const Pullach = lazy(() => import("./pages/standorte/Pullach"));
const Einsatzgebiete = lazy(() => import("./pages/standorte/Einsatzgebiete"));
const AngebotAngenommen = lazy(() => import("./pages/AngebotAngenommen"));
const AGBPrivatkunden = lazy(() => import("./pages/legal/AGBPrivatkunden"));
const AGBGeschaeftskunden = lazy(
  () => import("./pages/legal/AGBGeschaeftskunden"),
);
const AGB = lazy(() => import("./pages/legal/AGB"));
const Widerruf = lazy(() => import("./pages/legal/Widerruf"));
const WiderrufUebersicht = lazy(
  () => import("./pages/legal/WiderrufUebersicht"),
);
const Impressum = lazy(() => import("./pages/legal/Impressum"));
const Datenschutz = lazy(() => import("./pages/legal/Datenschutz"));
const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Suspense
          fallback={
            <div className="flex min-h-screen items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-r-transparent"></div>
            </div>
          }
        >
          <Routes>
            <Route
              path="/"
              element={
                <Layout>
                  <Index />
                </Layout>
              }
            />
            <Route
              path="/leistungen"
              element={
                <Layout>
                  <Leistungen />
                </Layout>
              }
            />
            <Route
              path="/leistungen/haushaltsreinigung"
              element={
                <Layout>
                  <Haushaltsreinigung />
                </Layout>
              }
            />
            <Route
              path="/leistungen/haushaltsreinigung-v2"
              element={
                <Layout>
                  <HaushaltsreinigungV2 />
                </Layout>
              }
            />
            <Route
              path="/leistungen/bueroreinigung"
              element={
                <Layout>
                  <Bueroreinigung />
                </Layout>
              }
            />
            <Route
              path="/leistungen/praxisreinigung"
              element={
                <Layout>
                  <Praxisreinigung />
                </Layout>
              }
            />
            <Route
              path="/leistungen/baureinigung"
              element={
                <Layout>
                  <Baureinigung />
                </Layout>
              }
            />
            <Route
              path="/leistungen/buegelservice"
              element={
                <Layout>
                  <Buegelservice />
                </Layout>
              }
            />
            <Route
              path="/leistungen/fensterreinigung"
              element={<Navigate to="/leistungen/glasreinigung" replace />}
            />
            <Route
              path="/leistungen/glasreinigung"
              element={
                <Layout>
                  <Fensterreinigung />
                </Layout>
              }
            />
            <Route
              path="/leistungen/grundreinigung"
              element={
                <Layout>
                  <Grundreinigung />
                </Layout>
              }
            />
            <Route
              path="/leistungen/umzugreinigung"
              element={
                <Layout>
                  <Umzugreinigung />
                </Layout>
              }
            />
            <Route
              path="/leistungen/umzugsreinigung"
              element={<Navigate to="/leistungen/umzugreinigung" replace />}
            />
            <Route
              path="/leistungen/steinreinigung"
              element={
                <Layout>
                  <Steinreinigung />
                </Layout>
              }
            />
            <Route
              path="/leistungen/polsterreinigung"
              element={
                <Layout>
                  <Polsterreinigung />
                </Layout>
              }
            />
            <Route
              path="/leistungen/treppenhausreinigung"
              element={
                <Layout>
                  <Treppenhausreinigung />
                </Layout>
              }
            />
            <Route
              path="/faq"
              element={
                <Layout>
                  <FAQ />
                </Layout>
              }
            />
            <Route
              path="/kontakt"
              element={
                <Layout>
                  <Kontakt />
                </Layout>
              }
            />
            <Route
              path="/preise"
              element={
                <Layout>
                  <Preise />
                </Layout>
              }
            />
            <Route
              path="/blog"
              element={
                <Layout>
                  <Blog />
                </Layout>
              }
            />
            <Route
              path="/blog/haushaltsreinigung-muenchen-checkliste-2025"
              element={
                <Layout>
                  <BlogArtikel1 />
                </Layout>
              }
            />
            <Route
              path="/blog/bueroreinigung-muenchen-produktivitaet"
              element={
                <Layout>
                  <BlogArtikel2 />
                </Layout>
              }
            />
            <Route
              path="/blog/bauendreinigung-muenchen"
              element={
                <Layout>
                  <BlogArtikel3 />
                </Layout>
              }
            />
            <Route
              path="/blog/wohnungsuebergabe-reinigung-checkliste"
              element={
                <Layout>
                  <BlogArtikel4 />
                </Layout>
              }
            />
            <Route
              path="/blog/putzhilfe-vs-reinigungsservice-unterschied"
              element={
                <Layout>
                  <BlogArtikel5 />
                </Layout>
              }
            />
            <Route
              path="/blog/fruehjahrputz-tipps-profi"
              element={
                <Layout>
                  <BlogArtikel6 />
                </Layout>
              }
            />
            <Route
              path="/termin"
              element={
                <Layout>
                  <TerminVerschieben />
                </Layout>
              }
            />
            <Route
              path="/standorte"
              element={
                <Layout>
                  <Einsatzgebiete />
                </Layout>
              }
            />
            <Route
              path="/standorte/"
              element={
                <Layout>
                  <Einsatzgebiete />
                </Layout>
              }
            />
            <Route
              path="/standorte/graefelfing"
              element={
                <Layout>
                  <Graefelfing />
                </Layout>
              }
            />
            <Route
              path="/standorte/graefelfing/"
              element={
                <Layout>
                  <Graefelfing />
                </Layout>
              }
            />
            <Route
              path="/standorte/planegg"
              element={
                <Layout>
                  <Planegg />
                </Layout>
              }
            />
            <Route
              path="/standorte/planegg/"
              element={
                <Layout>
                  <Planegg />
                </Layout>
              }
            />
            <Route
              path="/standorte/gauting"
              element={
                <Layout>
                  <Gauting />
                </Layout>
              }
            />
            <Route
              path="/standorte/gauting/"
              element={
                <Layout>
                  <Gauting />
                </Layout>
              }
            />
            <Route
              path="/standorte/neuried"
              element={
                <Layout>
                  <Neuried />
                </Layout>
              }
            />
            <Route
              path="/standorte/neuried/"
              element={
                <Layout>
                  <Neuried />
                </Layout>
              }
            />
            <Route
              path="/standorte/pullach"
              element={
                <Layout>
                  <Pullach />
                </Layout>
              }
            />
            <Route
              path="/standorte/pullach/"
              element={
                <Layout>
                  <Pullach />
                </Layout>
              }
            />
            <Route
              path="/einsatzgebiete"
              element={
                <Layout>
                  <Einsatzgebiete />
                </Layout>
              }
            />
            <Route
              path="/agb"
              element={
                <Layout>
                  <AGB />
                </Layout>
              }
            />
            <Route
              path="/agb-privatkunden"
              element={
                <Layout>
                  <AGBPrivatkunden />
                </Layout>
              }
            />
            <Route
              path="/agb-geschaeftskunden"
              element={
                <Layout>
                  <AGBGeschaeftskunden />
                </Layout>
              }
            />
            <Route
              path="/widerruf-uebersicht"
              element={
                <Layout>
                  <WiderrufUebersicht />
                </Layout>
              }
            />
            <Route
              path="/widerruf"
              element={
                <Layout>
                  <Widerruf />
                </Layout>
              }
            />
            <Route
              path="/widerrufsbelehrung"
              element={
                <Layout>
                  <Widerruf />
                </Layout>
              }
            />
            <Route
              path="/widerrufsformular"
              element={
                <Layout>
                  <Widerruf />
                </Layout>
              }
            />
            <Route
              path="/impressum"
              element={
                <Layout>
                  <Impressum />
                </Layout>
              }
            />
            <Route
              path="/datenschutz"
              element={
                <Layout>
                  <Datenschutz />
                </Layout>
              }
            />
            <Route path="/angebot-angenommen" element={<AngebotAngenommen />} />
            <Route path="/ueber-uns" element={<Navigate to="/" replace />} />
            {/* 301-style redirects for old URLs */}
            <Route
              path="/service/haushaltsreinigung"
              element={<Navigate to="/leistungen/haushaltsreinigung" replace />}
            />
            <Route
              path="/service/haushaltsreinigung/"
              element={<Navigate to="/leistungen/haushaltsreinigung" replace />}
            />
            <Route
              path="/service/buroreinigung"
              element={<Navigate to="/leistungen/bueroreinigung" replace />}
            />
            <Route
              path="/service/buroreinigung/"
              element={<Navigate to="/leistungen/bueroreinigung" replace />}
            />
            <Route
              path="/service/fensterreinigung"
              element={<Navigate to="/leistungen/glasreinigung" replace />}
            />
            <Route
              path="/service/fensterreinigung/"
              element={<Navigate to="/leistungen/glasreinigung" replace />}
            />
            <Route
              path="/service/grundreinigung"
              element={<Navigate to="/leistungen/grundreinigung" replace />}
            />
            <Route
              path="/service/grundreinigung/"
              element={<Navigate to="/leistungen/grundreinigung" replace />}
            />
            <Route
              path="/service/bauendreinigung"
              element={<Navigate to="/leistungen/baureinigung" replace />}
            />
            <Route
              path="/service/bauendreinigung/"
              element={<Navigate to="/leistungen/baureinigung" replace />}
            />
            <Route
              path="/service/praxisreinigung"
              element={<Navigate to="/leistungen/praxisreinigung" replace />}
            />
            <Route
              path="/service/praxisreinigung/"
              element={<Navigate to="/leistungen/praxisreinigung" replace />}
            />
            <Route
              path="/service/treppenhausreinigung"
              element={
                <Navigate to="/leistungen/treppenhausreinigung" replace />
              }
            />
            <Route
              path="/service/treppenhausreinigung/"
              element={
                <Navigate to="/leistungen/treppenhausreinigung" replace />
              }
            />
            <Route
              path="/service/steinreinigung"
              element={<Navigate to="/leistungen/steinreinigung" replace />}
            />
            <Route
              path="/service/steinreinigung/"
              element={<Navigate to="/leistungen/steinreinigung" replace />}
            />
            <Route
              path="/service/polster-und-teppichreinigung"
              element={<Navigate to="/leistungen/polsterreinigung" replace />}
            />
            <Route
              path="/service/polster-und-teppichreinigung/"
              element={<Navigate to="/leistungen/polsterreinigung" replace />}
            />
            <Route
              path="/service/bugelhilfe"
              element={<Navigate to="/leistungen/buegelservice" replace />}
            />
            <Route
              path="/service/bugelhilfe/"
              element={<Navigate to="/leistungen/buegelservice" replace />}
            />
            <Route path="/faqs" element={<Navigate to="/faq" replace />} />
            <Route path="/faqs/" element={<Navigate to="/faq" replace />} />
            <Route
              path="/privatkunden"
              element={<Navigate to="/leistungen/haushaltsreinigung" replace />}
            />
            <Route
              path="/privatkunden/"
              element={<Navigate to="/leistungen/haushaltsreinigung" replace />}
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
